import React, { useState, useMemo } from 'react'
import { Box, Button, CircularProgress } from '@mui/material'
import { AlertDialog, useGlobalMessageHandler } from 'components'
import { type HttpResponse } from 'interfaces'
import { type BaseStaffInformation } from 'services/person/us/edit/memberInfoService'
import { type BulkAssignResult } from 'services/member/memberStaffService'

const STAFFER_PREVIEW_COUNT = 5

interface BulkAssignButtonProps {
  ownerDisplayName: string
  targetLabel: string
  buttonLabel: string
  dialogTitle: string
  staffers: BaseStaffInformation[]
  hasSourceAddress: boolean
  onConfirm: () => Promise<HttpResponse<BulkAssignResult>>
  onSuccess?: (affected: number, stafferIds: number[]) => void | Promise<void>
}

const stafferName = (staffer: BaseStaffInformation): string => {
  const first = staffer.person?.firstName ?? ''
  const last = staffer.person?.lastName ?? ''
  const combined = `${first} ${last}`.trim()
  return combined.length > 0 ? combined : `Staffer #${staffer.id}`
}

export const BulkAssignButton: React.FC<BulkAssignButtonProps> = ({
  ownerDisplayName,
  targetLabel,
  buttonLabel,
  dialogTitle,
  staffers,
  hasSourceAddress,
  onConfirm,
  onSuccess
}: BulkAssignButtonProps): JSX.Element | null => {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()

  const stafferNames = useMemo(() => staffers.map(stafferName), [staffers])

  if (!hasSourceAddress || staffers.length === 0) return null

  const handleConfirm = (): void => {
    if (submitting) return
    setSubmitting(true)
    onConfirm()
      .then(async (response) => {
        if (response.succeeded && response.result != null) {
          showSuccessMessage(`Updated ${response.result.affected} staffer(s)`)
          if (onSuccess != null) {
            await onSuccess(response.result.affected, response.result.stafferIds)
          }
        } else {
          const message = response.errors != null
            ? Object.values(response.errors).join('\n')
            : (response.message ?? 'Bulk assign failed')
          showErrorMessage(message)
        }
      })
      .catch((error) => {
        const data = error?.response?.data
        const message = data?.errors != null
          ? Object.values(data.errors as Record<string, string>).join('\n')
          : (error?.message ?? 'Bulk assign failed')
        showErrorMessage(message)
      })
      .finally(() => { setSubmitting(false) })
  }

  const previewNames = stafferNames.slice(0, STAFFER_PREVIEW_COUNT)
  const remaining = stafferNames.length - previewNames.length
  const boldStafferList = remaining > 0
    ? `${previewNames.join(', ')}, and ${remaining} more`
    : previewNames.join(', ')

  const contentText = (
    <>
      This will update Address, Phone, and Fax for {staffers.length} staffer(s) attached to <strong>{ownerDisplayName}&apos;s {targetLabel}</strong>. Email and social-media URLs will not be changed. Staffers: <strong>{boldStafferList}</strong>.
    </>
  )

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        disabled={submitting}
        onClick={() => { setOpen(true) }}
        sx={{ position: 'relative' }}
      >
        <Box component="span" sx={{ visibility: submitting ? 'hidden' : 'visible' }}>
          {buttonLabel}
        </Box>
        {submitting && (
          <CircularProgress
            size={16}
            color="inherit"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-8px',
              marginLeft: '-8px'
            }}
          />
        )}
      </Button>
      <AlertDialog
        open={open}
        onClose={() => { setOpen(false) }}
        onButtonClick={handleConfirm}
        title={dialogTitle}
        contentText={contentText}
        nameButtonCancel="Cancel"
        nameButtonConfirm="Apply"
        containedButton={true}
      />
    </>
  )
}
